import React from 'react'
import { DropdownButton } from '@/shared/ui/dropdown-button'
import { DropdownItem } from '@/shared/ui/dropdown-item'
import { NodeModel } from '@minoru/react-dnd-treeview'
import DeleteSvg from '@/shared/assets/images/Trash.svg'
import AccrualSvg from '@/shared/assets/images/Coins.svg'
import ArrowDownSvg from '@/shared/assets/images/ArrowDown.svg'
import VisibilityAclSvg from '@/shared/assets/images/FileLock.svg'
import PushPinSvg from '@/shared/assets/images/PushPin.svg'
import { GroupNode } from '@/entities/groups'
import { useUpdateGroupModal } from '@/features/update-group'
import { RoleType } from '@/shared/api/users'
import { TargetType } from '@/shared/api/groups'

interface TreeGroupNodeProps {
    node: NodeModel<GroupNode>
    depth: number
    onToggle: () => void
    isOpen: boolean
    role: RoleType
    defaultGroupId?: string | null
    onSetDefaultGroup?: (groupId: string) => void | Promise<void>
}

const DefaultGroupBadge: React.FC = () => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-color-primary/10 text-color-primary text-xs font-semibold uppercase whitespace-nowrap">
        <PushPinSvg width={12} height={12} />
        Основная
    </span>
)

export const TreeGroupNode: React.FC<TreeGroupNodeProps> = ({
    depth,
    onToggle,
    role,
    isOpen,
    node,
    defaultGroupId,
    onSetDefaultGroup
}) => {
    const { openDeleteGroupModal, openGroupAccrualModal, openVisibilityAclModal } = useUpdateGroupModal()
    const isDefaultGroup = !!defaultGroupId && defaultGroupId === node.data.id
    const canSetDefault = !!onSetDefaultGroup && !isDefaultGroup

    const renderActions = () => (
        <DropdownButton>
            {canSetDefault && (
                <DropdownItem
                    icon={PushPinSvg}
                    label="Сделать основной"
                    onClick={() => onSetDefaultGroup?.(node.data.id)}
                />
            )}
            <DropdownItem
                icon={DeleteSvg}
                label="Удалить группу"
                onClick={() => openDeleteGroupModal(node.data.id, role)}
            />
            <DropdownItem
                icon={AccrualSvg}
                label="Начислить бонусы"
                onClick={() => openGroupAccrualModal(node.data.id)}
            />
            <DropdownItem
                icon={VisibilityAclSvg}
                label="Управление видимостью ACL"
                onClick={() => openVisibilityAclModal({ id: node.data.id }, TargetType.GROUP)}
            />
        </DropdownButton>
    )

    return (
        depth === 0
            ? <div className={`bg-white ${isOpen ? 'border-b border-[#E7EAEE]' : ''}`}>
                <div className="flex justify-between items-center gap-3">
                    <div className="px-2 py-4 text-lg font-semibold flex items-center gap-2 min-w-0">
                        <span className="truncate">{node.text}</span>
                        {isDefaultGroup && <DefaultGroupBadge />}
                    </div>
                    <div className="flex gap-3 items-center">
                        <button onClick={onToggle}>
                            <ArrowDownSvg width={24} height={24} className={`transition ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                        </button>
                        {renderActions()}
                    </div>
                </div>
            </div>
            : <div className={`pt-3`}>
                <div 
                    style={{
                        margin: `0 ${depth * 4}px`
                    }} 
                    className={`rounded-[4px] bg-[#F8F8F8] border-b border-[#E7EAEE] px-[14px] py-4`
                }>
                    <div className="flex justify-between items-center gap-3">
                        <div className="font-semibold text-lg flex items-center gap-2 min-w-0">
                            <span className="truncate">{node.text}</span>
                            {isDefaultGroup && <DefaultGroupBadge />}
                        </div>
                        <div className="flex gap-3 items-center">
                            <button onClick={onToggle}>
                                <ArrowDownSvg width={24} height={24} className={`transition ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                            </button>
                            {renderActions()}
                        </div>
                    </div>
                </div>
            </div>
    )
}
